import React, { Fragment, useEffect, useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import {
  Filter,
  FilterExtraAction
} from '../../components/_WMS/FilterComponent/types'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { useSnackbar } from '../../hooks/useSnackbar'
import { Status } from '../../hooks/useFetchState'
import { ApiResponse } from '../../typings/ApiResponse'
import { formatExpirationDate } from '../../utils/dateHelper'
import {
  KardexSearch,
  hasRequiredFilter,
  isDateRangeValid
} from './KardexSearch'
import { getColumnsConfig } from './config/tableColumns'
import { getKardexList, exportKardexToExcel } from './config/api'
import { StockMovementKardexInfo } from './types'
import { eTypePosition } from '../../enums/eTypePosition'

// Detalle expandible: tira horizontal de campos del DTO que no están en la
// grilla. La data viene de la propia fila, sin llamada extra a la API.
// Entrada (typeMovementeId === 3) → verde; Salida (4) → rojo
const isEntrada = (row: StockMovementKardexInfo) => row.typeMovementeId === 3

// Si el tipo de posición es 1, no se aplica color (queda el color por defecto)
const hasDirectionColor = (row: StockMovementKardexInfo) =>
  row.typePositionId !== eTypePosition.Draft

const KardexExpandableContent: React.FC<{ row: StockMovementKardexInfo }> = ({
  row
}) => {
  const backgroundColor = !hasDirectionColor(row)
    ? undefined
    : isEntrada(row)
    ? '#eef7ee'
    : '#fdeeee'

  const isEmpty = (value: React.ReactNode) =>
    value === null || value === undefined || value === ''

  // Trunca a 30 caracteres con "..." y muestra el texto completo en tooltip al pasar el mouse
  const truncated = (text: string, max = 30) => {
    if (text.length <= max) return <span>{text}</span>
    return (
      <Tooltip title={text}>
        <span>{`${text.slice(0, max)}...`}</span>
      </Tooltip>
    )
  }

  const field = (label: string, value: React.ReactNode) => (
    <Box style={{ minWidth: '140px' }}>
      <Typography
        style={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.5px',
          color: '#8a8a8a',
          textTransform: 'uppercase',
          marginBottom: '4px'
        }}
      >
        {label}
      </Typography>
      <Typography style={{ fontSize: '12px', color: '#333' }}>
        {isEmpty(value) ? '—' : value}
      </Typography>
    </Box>
  )

  return (
    <Box
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        // Cancela el padding (16px) del contenedor del expandible para pintar
        // el panel completo del color de la dirección, sin dejar marco gris.
        margin: '-16px',
        padding: '10px 14px',
        backgroundColor
      }}
    >
      {field(
        'Descripción',
        isEmpty(row.descripcion) ? null : truncated(row.descripcion)
      )}
      {field('OT', row.workOrderId)}
      {field('Usuario', row.userAlias)}
      {field('ID Caja / LPN', row.referenceNumber)}
      {field('Tipo Ubicación', row.typePositionName)}
      {field('Lote', row.batchNumber)}
      {field(
        'Vencimiento',
        row.batchExpirationDate
          ? formatExpirationDate(row.batchExpirationDate, 'dd/MM/yyyy')
          : null
      )}
      {field('Observaciones', row.observation)}
    </Box>
  )
}

export const KardexPage: React.FC = () => {
  const { callApi, state, setIdle } = useApi<
    ApiResponse<StockMovementKardexInfo>
  >()
  const pagination = usePagination(10)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const { callApi: callApiKardex, state: stateExportStock } = useApi()

  const columnsConfig = getColumnsConfig()

  const requestData = (filters: Filter[], page: number, pageSize: number) => {
    // Solo buscar si hay un filtro obligatorio y el rango de fechas es válido
    if (!hasRequiredFilter(filters) || !isDateRangeValid(filters)) return
    callApi(getKardexList(filters, page, pageSize)).catch((error) => {
      openSnackBar(error as any, false)
    })
  }

  const resetAndFetchData = (filters: Filter[]) => {
    pagination.setPagination(0, pagination.pageSize)
    requestData(filters, 0, pagination.pageSize)
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  // Sin búsqueda al ingresar: la grilla queda en idle hasta aplicar filtros.
  // Al limpiar todos los filtros se vuelve al estado inicial.
  useEffect(() => {
    if (selectedFilters.length === 0) {
      pagination.setPagination(0, pagination.pageSize)
      setIdle()
    }
  }, [selectedFilters])

  // Agenda la exportación del Kardex en segundo plano con los filtros seleccionados
  const handleExport = () => {
    callApiKardex(exportKardexToExcel(selectedFilters))
      .then(() => {
        openSnackBar(`Se agendo proceso correctamente`, true)
      })
      .catch((err) => {
        openSnackBar(err, true)
      })
  }


  const extraActions: FilterExtraAction[] = [
    {
      label: 'Exportar',
      onClick: handleExport,
      icon: <DownloadIcon />,
      tooltip: 'Se agendará la exportación en segundo plano',
      disabled: stateExportStock.status === Status.Loading || !state.data?.items?.length
    }
  ]

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <KardexSearch
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={resetAndFetchData}
          extraActions={extraActions}
        />

        <GenericTableV2<StockMovementKardexInfo>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='stockMovementKardexId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          styling={{
            dense: false,
            hoverEffect: true,
            rowStyle: (row: StockMovementKardexInfo) =>
              !hasDirectionColor(row)
                ? {}
                : {
                    backgroundColor: isEntrada(row)
                      ? 'rgba(76, 175, 80, 0.10)'
                      : 'rgba(229, 57, 53, 0.10)'
                  }
          }}
          expandable={{
            getDetailData: (row: StockMovementKardexInfo) => row,
            renderContent: (_, row: StockMovementKardexInfo) => (
              <KardexExpandableContent row={row} />
            ),
            headerText: 'Detalle',
            expansionTriggerMode: 'both',
            expandAllByDefault: false
          }}
        />
      </StandardPage>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
